import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { ErrorResponse } from '@/types/common'
import type { AxiosError } from 'axios'
import type { ProductFormData } from '@/lib/validations/products'
import type { ProductInput } from '@/types/product'
import { ProductFileType, ProductPriceType } from '@/types/product'
import FormProduct from '@/components/Products/Form'
import {
  categoryQueryOptions,
  productApisQueryOptions,
  subCategoryQueryOptions,
} from '@/lib/queryOptions'
import { ProductService } from '@/services/products'
import { translateText } from '@/lib/translate'

export const Route = createFileRoute('/_authenticated/products/create/')({
  component: CreateProduct,
})

function CreateProduct() {
  const queryClient = useQueryClient()
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')
  const [isTranslating, setIsTranslating] = useState(false)
  const navigate = useNavigate()

  const categoryQuery = useQuery(
    categoryQueryOptions({
      page: 1,
      take: 100,
      isSelectAll: true,
      parentId: null,
    }),
  )

  const subCategoryQuery = useQuery(
    subCategoryQueryOptions({
      page: 1,
      take: 100,
      isSelectAll: true,
      parentId: selectedCategoryId,
    }),
  )

  const productApisQuery = useQuery(productApisQueryOptions())

  const apiOptions =
    productApisQuery.data?.data.map((api) => ({
      value: api.id,
      label: api.name,
      url: api.url,
    })) || []

  const mutationCreateProduct = useMutation({
    mutationFn: (body: ProductInput) => {
      return ProductService.create([body])
    },
    onSuccess: () => {
      toast.success('Tạo sản phẩm thành công')
      queryClient.invalidateQueries({
        queryKey: ['products'],
      })
      navigate({ to: '/products' })
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message)
    },
  })

  const handleSubmit = async (
    data: Partial<ProductFormData>,
    _removeFileIds: any,
    formData: ProductFormData,
  ) => {
    const priceVnd = data.vnd
    const priceUsd = data.usd
    const viName =
      data.nameMode === 'same'
        ? data.englishName || ''
        : data.name || data.englishName || ''
    const translations: ProductInput['translations'] = [
      {
        language: 'vi',
        name: viName,
        summary: data.shortDescription || '',
        description: data.description || '',
      },
    ]

    setIsTranslating(true)
    try {
      const [summaryEn, descriptionEn] = await Promise.all([
        data.shortDescription
          ? translateText(data.shortDescription, { source: 'vi', target: 'en' })
          : Promise.resolve(''),
        data.description
          ? translateText(data.description, { source: 'vi', target: 'en' })
          : Promise.resolve(''),
      ])

      const englishTranslation = {
        language: 'en',
        name: data.englishName || data.name || '',
        summary: summaryEn || data.shortDescription || '',
        description: descriptionEn || data.description || '',
      }

      if (
        englishTranslation.name ||
        englishTranslation.summary ||
        englishTranslation.description
      ) {
        translations.push(englishTranslation)
      }
    } catch (error) {
      console.error('Create product translation failed', error)
      toast.error('Không thể dịch sang tiếng Anh. Vui lòng thử lại.')
      translations.push({
        language: 'en',
        name: data.englishName || data.name || '',
        summary: data.shortDescription || '',
        description: data.description || '',
      })
    } finally {
      setIsTranslating(false)
    }

    const payload: ProductInput = {
      code: data.stt || '',
      status: data.displayStatus || '',
      releaseStatus: data.productCondition || '',
      pinStatus: data.pinStatus || '',
      hashTags: data.hashtag || [],
      updateSupportQuota: Number(data.appUpdateSupportCount) || 0,
      updateSupportPeriodMonths: Number(data.appUpdateSupportDuration) || 0,
      guideId: undefined,
      mainCategoryId: data.category || '',
      subCategoryIds: data.subCategoryId ? [data.subCategoryId] : [],
      createThumbnail:
        formData.thumbnail.length > 0
          ? {
              publicId: formData.thumbnail[0].publicId,
              resourceType: formData.thumbnail[0].resourceType,
              format: formData.thumbnail[0].format,
              bytes: formData.thumbnail[0].bytes || 0,
              width: formData.thumbnail[0].width || 0,
              height: formData.thumbnail[0].height || 0,
              folder: formData.thumbnail[0].folder,
              originalFilename: formData.thumbnail[0].originalFilename,
              productFileType: ProductFileType.THUMBNAIL,
            }
          : undefined,
      createBannerImages: (formData.bannerImages || []).map((file: any) => ({
        publicId: file.publicId,
        resourceType: file.resourceType,
        format: file.format,
        bytes: file.bytes || 0,
        width: file.width || 0,
        height: file.height || 0,
        folder: file.folder,
        originalFilename: file.originalFilename,
        productFileType: ProductFileType.BANNER,
      })),
      createIllustrationImages: (formData.illustrationImages || []).map(
        (file: any) => ({
          publicId: file.publicId,
          resourceType: file.resourceType,
          format: file.format,
          bytes: file.bytes || 0,
          width: file.width || 0,
          height: file.height || 0,
          folder: file.folder,
          originalFilename: file.originalFilename,
          productFileType: ProductFileType.ILLUSTRATION,
        }),
      ),
      prices:
        data.priceType === ProductPriceType.MANUAL
          ? [
              {
                currency: 'vnd',
                type: data.priceType,
                originalAmount: priceVnd?.appPrice
                  ? Number(priceVnd.appPrice)
                  : 0,
                amount: priceVnd?.sellingPrice
                  ? Number(priceVnd.sellingPrice)
                  : 0,
                discountPercent: priceVnd?.discount
                  ? Number(priceVnd.discount)
                  : 0,
              },
              {
                currency: 'usd',
                type: data.priceType,
                originalAmount: priceUsd?.appPrice
                  ? Number(priceUsd.appPrice)
                  : 0,
                amount: priceUsd?.sellingPrice
                  ? Number(priceUsd.sellingPrice)
                  : 0,
                discountPercent: priceUsd?.discount
                  ? Number(priceUsd.discount)
                  : 0,
              },
            ]
          : [],
      apiIds: [data.api1, data.api2, data.api3].filter(Boolean) as string[],
      translations,
    }

    try {
      await mutationCreateProduct.mutateAsync(payload)
    } catch (error) {
      // onError handler already displays toast
      console.error('Create product failed', error)
    }
  }

  const isLoading =
    categoryQuery.isLoading ||
    subCategoryQuery.isLoading ||
    productApisQuery.isLoading

  return (
    <FormProduct
      initialData={undefined}
      onSubmit={handleSubmit}
      setSelectedCategoryId={setSelectedCategoryId}
      categories={categoryQuery.data?.data.items || []}
      subCategories={subCategoryQuery.data?.data.items || []}
      productApis={apiOptions}
      isLoading={isLoading}
      disableSubmit={mutationCreateProduct.isPending || isTranslating}
      label="Thêm sản phẩm"
      isCreate={true}
    />
  )
}
